import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import CommonImage from '@components/CommonImage';
import DeleteConfirmationModal from '@components/DeleteConfirmationModal';
import { toastSuccess } from '@components/Toast';
import CustomDropdown from '@components/CustomDropdown';

import DesignCard from '@features/favourites/components/DesignCard';
import FavouriteCollectionCard from '@features/favourites/components/FavouriteCollectionCard';
import {
	buildRequestBody,
	FAVOURITES_DETAILS_SIDE_PANEL_TABS,
	getUpdatedProductList
} from '@features/favourites/utils/favouriteUtil';
import AsyncSearch from '@features/filter/Common/AsyncSearch';

import { clearDesignViewModalInfo, setDesignViewModalInfo } from '@store/actions/DesignViewModalAction';
import { clearGlobalFilter, clearGlobalFilterWithExcludeField } from '@store/actions/FilterAction';
import { closeLoader, openLoader } from '@store/actions/LoaderAction';
import { likeProduct } from '@store/design/action';
import { getFavouriteProducts } from '@store/reducers';

import {
	getCollectionList,
	getFavouriteSidePanelStyles,
	getLinkedCollections,
	linkCollection,
	unlinkCollection
} from '@services/favouriteService';

import { isAdmin, isPageReachBottom, printApiError, replaceUnderLine } from '@/utils/Util';
import { fetchBrandWiseBuyers, fetchCollectionPurpose } from '@/services/collectionService';

const SidePanel = ({
	setIsShowSidePanel,
	selectedSortingOption,
	fetchFavouriteProducts,
	viewType,
	setProductList,
	handleSortingOptionChange,
	productList
}) => {
	const [activeTab, setActiveTab] = useState('STYLE');
	const [collectionsList, setCollectionsList] = useState({ data: [], totalElements: 0 });
	const [linkedItems, setLinkedItems] = useState([]);
	const filterfields = useSelector((store) => store.filterFieldStore);
	const dispatch = useDispatch();
	const { id } = useParams();
	const [searchKey, setSearchKey] = useState('');
	const [searchTimeout, setSearchTimeout] = useState(null);
	const { searchResult, isViwTechpack } = useSelector((store) => store.collectionSearchOptionStore);
	const [designList, setDesignList] = useState([]);
	const [selectedLinkedItemId, setSelectedLinkedItemId] = useState(null);
	const [QRLikedDesignIds, setQRLikedDesignIds] = useState([]);
	let allFavouritesForProducts = useSelector(getFavouriteProducts);
	const [selectedCreatedBy, setSelectedCreatedBy] = useState(null);
	const [selectedCreationPurpose, setSelectedCreationPurpose] = useState(null);
	const [createdByList, setCreatedByList] = useState([]);
	const [creationPurposeList, setCreationPurposeList] = useState([]);
	const [page, setPage] = useState(0);

	const collectionRef = useRef({ data: [], currentPage: 0, totalElements: 0, totalPages: 0 });
	const collectionContainerRef = useRef(null);

	useEffect(() => {
		handleSortingOptionChange({ code: 'NEWEST', title: 'Newest first' });
		getCollectionPurpose();
		getCreatedByList();
	}, []);

	const getCreatedByList = async () => {
		try {
			const params = '?page=0&size=1000&isNitexUser=true';
			const response = await fetchBrandWiseBuyers(params);
			const createdBy = response.map((item) => ({ ...item, value: item?.name, label: item?.name }));
			setCreatedByList(createdBy);
		} catch (error) {
			printApiError(error);
		}
	};
	const getCollectionPurpose = async () => {
		try {
			const response = await fetchCollectionPurpose();
			setCreationPurposeList(response);
		} catch (error) {
			printApiError(error);
		}
	};

	const isLinked = (collectionId) => {
		const findLinkedCollection = linkedItems?.find(
			(item) => parseInt(item?.id) === parseInt(collectionId)
		);
		if (findLinkedCollection) {
			return true;
		} else return false;
	};

	const fetchProducts = async () => {
		try {
			dispatch(openLoader());
			let requestBody = buildRequestBody(filterfields, id);
			let response = await getFavouriteSidePanelStyles(requestBody);
			if (response?.data) {
				if (isViwTechpack) {
					openDesignViewModal({ id: response?.data[0]?.id });
				}
				// if (isAddToFavourite) {
				// 	response.data = response.data.map((design) => {
				// 		if (parseInt(design?.id) === parseInt(response?.data[0]?.id)) {
				// 			return { ...design, liked: true };
				// 		}
				// 		return design;
				// 	});
				// 	dispatch(likeProduct(response?.data[0]?.id, handleSuccessLikeProduct, viewType, id));
				// }
				setDesignList(response.data);
			} else {
				setDesignList([]);
			}
		} catch (error) {
			printApiError(error);
			setDesignList([]);
		} finally {
			dispatch(closeLoader());
		}
	};

	useEffect(() => {
		if (
			Object.keys(filterfields?.DETAILS_PAGE_FILTERS || {})?.length > 0 ||
			filterfields?.referenceNumber?.length > 0 ||
			filterfields?.productIdList?.length > 0
		) {
			fetchProducts();
		}
	}, [filterfields]);

	const onCloseViewModal = () => {
		dispatch(clearDesignViewModalInfo({}));
	};

	const openDesignViewModal = (item) => {
		dispatch(
			setDesignViewModalInfo({
				isShowMergeTechPack: false,
				id: item?.id,
				onCloseViewModal: onCloseViewModal,
				source: 'PRODUCT_LIST'
			})
		);
	};

	useEffect(() => {
		if (searchResult) {
			openDesignViewModal(searchResult);
		}
	}, [searchResult]);

	const fetchCollectionList = async (searchKey = '', createdBy = '', creationPurpose = '', pageNum = 0) => {
		try {
			dispatch(openLoader());
			const queryParm = `?page=${pageNum}&size=10&name=${encodeURIComponent(searchKey)}&createdByIdList=${createdBy}&creationPurposeTagId=${creationPurpose}`;
			const response = await getCollectionList(queryParm);
			if (response?.data) {
				const organizedData = response.data.map((item) => ({
					...item,
					documents: item?.documentPathList,
					brandResponse: { name: item?.brand, id: item?.brandId },
					createdAt: item?.dateCreated
				}));

				collectionRef.current = {
					data: pageNum === 0 ? organizedData : [...collectionRef.current.data, ...organizedData],
					currentPage: pageNum,
					totalElements: response?.totalElements || 0,
					totalPages: response?.totalPages || 0
				};

				setCollectionsList({
					data: collectionRef.current.data,
					totalElements: response?.totalElements || 0
				});
			} else {
				setCollectionsList({ data: [], totalElements: 0 });
			}
		} catch (error) {
			printApiError(error);
			setCollectionsList({ data: [], totalElements: 0 });
		} finally {
			dispatch(closeLoader());
		}
	};

	useEffect(() => {
		if (searchTimeout) {
			clearTimeout(searchTimeout);
		}

		if (searchKey || selectedCreatedBy?.id || selectedCreationPurpose?.id) {
			const timeoutId = setTimeout(() => {
				setPage(0);
				fetchCollectionList(searchKey, selectedCreatedBy?.id, selectedCreationPurpose?.id, 0);
			}, 500);
			setSearchTimeout(timeoutId);
		}

		return () => {
			if (searchTimeout) {
				clearTimeout(searchTimeout);
			}
		};
	}, [searchKey, selectedCreatedBy?.id, selectedCreationPurpose?.id]);

	useEffect(() => {
		if (page > 0) {
			fetchCollectionList(searchKey, selectedCreatedBy?.id, selectedCreationPurpose?.id, page);
		}
	}, [page]);

	const handleSuccessLikeProduct = (response) => {
		const isProductAlreadyInFav = isProductAlreadyInFavourites(
			response?.payload?.LikedProductList[0]?.productResponse?.id
		);
		if (isProductAlreadyInFav) {
			return;
		}

		toastSuccess('Added to favourites');

		const updatedProductList = getUpdatedProductList(allFavouritesForProducts, response);
		dispatch({
			type: 'FETCH_FAVOURITE_PRODUCT_LIST_SUCCESS',
			payload: {
				totalElements: allFavouritesForProducts?.totalElements + 1,
				totalPages: Math.ceil((allFavouritesForProducts?.totalElements + 1) / 30),
				currentPage: Math.ceil((allFavouritesForProducts?.totalElements + 1) / 30),
				data: [...response?.payload?.LikedProductList, ...updatedProductList]
			}
		});
	};

	const isProductAlreadyInFavourites = (productId) => {
		return allFavouritesForProducts?.data?.some(
			(product) => parseInt(product?.productResponse?.id) === parseInt(productId)
		);
	};

	const handleLikeProduct = async (productId) => {
		dispatch(likeProduct(productId, handleSuccessLikeProduct, viewType, id));
		const isProductAlreadyInFav = isProductAlreadyInFavourites(productId);
		if (isProductAlreadyInFav) {
			return;
		}
		const updatedDesignList = designList.map((design) => {
			if (parseInt(design?.id) === parseInt(productId)) {
				return { ...design, liked: true };
			}
			return design;
		});

		setDesignList(updatedDesignList);
	};

	const fetchLinkedCollections = async () => {
		try {
			dispatch(openLoader());
			const response = await getLinkedCollections(id);
			setLinkedItems(response);
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}
	};

	useEffect(() => {
		if (activeTab === 'LINKED_COLLECTION') {
			fetchLinkedCollections();
		}
	}, [activeTab]);

	const handleLinkCollection = async (collectionId) => {
		try {
			dispatch(openLoader());
			const response = await linkCollection(collectionId, id);
			if (response?.success) {
				fetchLinkedCollections();
				dispatch(fetchFavouriteProducts(0, 20, '', selectedSortingOption?.code, id));
			}
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}
	};

	const handleUnlinkCollection = async () => {
		try {
			dispatch(openLoader());
			const response = await unlinkCollection(selectedLinkedItemId, id);
			if (response?.success) {
				fetchLinkedCollections();
				dispatch(fetchFavouriteProducts(0, 20, '', selectedSortingOption?.code, id));
				toastSuccess('Unlinked Collection Successfully!');
				setSelectedLinkedItemId(null);
			}
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}
	};

	const handleClear = () => {
		if (activeTab === 'STYLE') {
			setDesignList([]);
			setQRLikedDesignIds([]);
			dispatch(clearGlobalFilter());
			dispatch(clearGlobalFilterWithExcludeField([]));
		} else if (activeTab === 'COLLECTION') {
			setCollectionsList({ data: [], totalElements: 0 });
			setSelectedCreatedBy(null);
			setSelectedCreationPurpose(null);
			setSearchKey('');
			collectionRef.current = { data: [], currentPage: 0, totalElements: 0, totalPages: 0 };
			setPage(0);
		} else if (activeTab === 'LINKED_COLLECTION') {
			setLinkedItems([]);
			setSearchKey('');
		}
	};

	const isShowClearButton = () => {
		if (activeTab === 'COLLECTION' || activeTab === 'LINKED_COLLECTION') {
			return searchKey !== '' || selectedCreatedBy?.id || selectedCreationPurpose?.id;
		} else {
			return designList?.length > 0;
		}
	};

	const closeSidePanel = () => {
		if (!selectedLinkedItemId) {
			handleClear();
			setIsShowSidePanel(false);
		}
	};

	const renderCountbasedOnTab = () => {
		if (activeTab === 'STYLE' && designList?.length > 0) {
			return `${designList?.length} ${designList?.length > 1 ? 'Styles' : 'Style'}`;
		} else if (activeTab === 'COLLECTION' && collectionsList.totalElements > 0) {
			return `${collectionsList.totalElements} ${
				collectionsList.totalElements > 1 ? 'Collections' : 'Collection'
			}`;
		} else if (activeTab === 'LINKED_COLLECTION' && linkedItems?.length > 0) {
			return `${linkedItems?.length} ${
				linkedItems?.length > 1 ? 'Linked Collections' : 'Linked Collection'
			}`;
		}
	};

	const handleScroll = () => {
		const { totalElements, currentPage, data } = collectionRef.current;
		const scrollContainer = collectionContainerRef.current;

		if (scrollContainer) {
			const isBottom = isPageReachBottom(scrollContainer);
			if (isBottom && totalElements > data.length) {
				setPage((currentPage ?? 0) + 1);
			}
		}
	};

	useEffect(() => {
		const scrollContainer = collectionContainerRef.current;
		if (scrollContainer) {
			scrollContainer.addEventListener('scroll', handleScroll);
		}
		return () => {
			if (scrollContainer) {
				scrollContainer.removeEventListener('scroll', handleScroll);
			}
		};
	}, []);

	return (
		<React.Fragment>
			<div className="add-style-sidebar shadow-8dp">
				<div className="sidebar-header">
					<div className="navigation-tab border-0 px-0 d-flex justify-content-between">
						<ul>
							{FAVOURITES_DETAILS_SIDE_PANEL_TABS.map((tab) => (
								<li
									key={tab}
									className={`${activeTab === tab ? 'active' : ''}`}
									onClick={() => setActiveTab(tab)}
								>
									{replaceUnderLine(tab)?.toUpperCase()}
								</li>
							))}
						</ul>
						<div>
							<CommonImage
								className="cursor-pointer"
								src="/icons/close.svg"
								width="24"
								alt=""
								onClick={closeSidePanel}
							/>
						</div>
					</div>
					<div className="search-filter pb-3">
						{activeTab === 'STYLE' ? (
							<div className="style-search-and-dropdown">
								<AsyncSearch
									folderId={id}
									onSuccessQRSearch={setProductList}
									parentComponent={'SidePanel'}
									viewType={viewType}
									shouldUpdateProductList={true}
									productList={productList}
									setQRLikedDesignIds={setQRLikedDesignIds}
									clearSidePanel={true}
									isHideChip={true}
								/>
							</div>
						) : (
							<div>
								<div className="with-icon right-icon">
									<input
										name={`search-${activeTab}`}
										type="text"
										placeholder="Collection name"
										value={searchKey}
										onChange={(e) => {
											setSearchKey(e.target.value);
										}}
									/>
									<span className="right-icon">
										<img src="/icons/search.png" alt="" />
									</span>
								</div>
								{activeTab === 'COLLECTION' && (
									<div className="d-flex gap-2 mt-3">
										<div className="col-md-6 p-0">
											<CustomDropdown
												title="Created by"
												type="addItem"
												items={createdByList}
												selectedItem={selectedCreatedBy?.value}
												onItemClick={(value) => {
													setSelectedCreatedBy(value);
												}}
											/>
										</div>
										<div className="col-md-6 pr-2">
											<CustomDropdown
												type="addItem"
												title="Creation purpose"
												items={creationPurposeList}
												selectedItem={selectedCreationPurpose?.value}
												onItemClick={(value) => {
													setSelectedCreationPurpose(value);
												}}
												isAddNew={isAdmin()}
											/>
										</div>
									</div>
								)}
							</div>
						)}
					</div>
					<div className="d-flex justify-content-between align-items-center align-self-center">
						<p className="regular-14 gray_dark_02">{renderCountbasedOnTab()}</p>
						{isShowClearButton() && (
							<p className="regular-14 gray_dark_01 cursor-pointer" onClick={handleClear}>
								Clear
							</p>
						)}
					</div>
				</div>
				<div className="sidebar-body mt-12 scroll-y-label" ref={collectionContainerRef}>
					<div className="style-items">
						{activeTab === 'STYLE' &&
							designList.map((design) => (
								<DesignCard
									key={design.id}
									design={design}
									onOpenDesignView={openDesignViewModal}
									onLikeProduct={handleLikeProduct}
									QRLikedDesignIds={QRLikedDesignIds}
								/>
							))}

						{activeTab === 'COLLECTION' &&
							collectionsList.data.map((collection) => (
								<FavouriteCollectionCard
									key={collection?.id}
									folder={collection}
									isCollectionTab
									onLink={handleLinkCollection}
									onUnLink={() => setSelectedLinkedItemId(collection?.id)}
									isLinked={isLinked}
									createdBy={collection?.createdBy}
									numberOfProducts={collection?.numOfDesign}
								/>
							))}
						{activeTab === 'LINKED_COLLECTION' &&
							linkedItems?.map((item) => (
								<FavouriteCollectionCard
									key={item?.id}
									folder={{ ...item, documents: item?.documentPathList }}
									isLinkedTab
									onUnLink={() => setSelectedLinkedItemId(item?.id)}
									isLinked={isLinked}
									createdBy={item?.createdBy}
									numberOfProducts={item?.numOfDesign}
								/>
							))}
					</div>
					{((activeTab === 'STYLE' && designList.length === 0) ||
						(activeTab === 'COLLECTION' && collectionsList.data.length === 0)) && (
						<div className="empty-content text-center h-100 d-flex flex-column justify-content-center">
							<p className="regular-16 gray_dark_03 pb-1">
								{' '}
								Search {activeTab.toLowerCase()} &{' '}
								{activeTab === 'COLLECTION' ? 'linked' : 'add'} to this folder
							</p>
						</div>
					)}
				</div>
			</div>
			{selectedLinkedItemId && (
				<DeleteConfirmationModal
					onClose={() => setSelectedLinkedItemId(null)}
					onConfirm={handleUnlinkCollection}
					title="Unlink Collection"
					description="Are you sure about unlink this collection?"
				/>
			)}
		</React.Fragment>
	);
};

export default SidePanel;
